import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, Download, Upload, FileSpreadsheet } from 'lucide-react';
import { API_BASE } from '../../lib/api';

export default function QuestionManagement() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    question_text: '', option_a: '', option_b: '', option_c: '', option_d: '',
    correct_option: 'a', explanation: '', image_url: '', subject: '', marks: 1
  });

  useEffect(() => { loadQuestions(); }, [testId]);

  async function loadQuestions() {
    try { const { data } = await api.get(`/admin/tests/${testId}/questions`); setQuestions(data); } catch {} finally { setLoading(false); }
  }

  function openCreate() {
    setEditing(null);
    setForm({ question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', correct_option: 'a', explanation: '', image_url: '', subject: '', marks: 1 });
    setDialogOpen(true);
  }

  function openEdit(q) {
    setEditing(q);
    setForm({
      question_text: q.question_text, option_a: q.option_a, option_b: q.option_b,
      option_c: q.option_c, option_d: q.option_d, correct_option: q.correct_option,
      explanation: q.explanation || '', image_url: q.image_url || '', subject: q.subject || '', marks: q.marks || 1
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.question_text.trim() || !form.option_a.trim()) { toast.error('Question and options required'); return; }
    try {
      if (editing) {
        await api.put(`/admin/questions/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post(`/admin/tests/${testId}/questions`, form);
        toast.success('Added');
      }
      setDialogOpen(false);
      loadQuestions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this question?')) return;
    try { await api.delete(`/admin/questions/${id}`); toast.success('Deleted'); loadQuestions(); } catch { toast.error('Failed'); }
  }

  async function handleExport() {
    try {
      const response = await api.get(`/admin/tests/${testId}/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `questions_${testId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch { toast.error('Export failed'); }
  }

  async function handleDownloadTemplate() {
    try {
      const response = await api.get('/admin/tests/template-excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'question_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  }

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) { toast.error('Only .xlsx files supported'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/admin/tests/${testId}/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${data.imported} questions`);
      if (data.errors?.length > 0) {
        toast.error(`${data.errors.length} rows had errors`);
        console.warn('Import errors:', data.errors);
      }
      loadQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  return (
    <div data-testid="question-management">
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)} data-testid="back-to-tests">
        <ArrowLeft size={16} className="mr-2" /> Back
      </Button>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Questions</h1>
          <p className="text-sm text-[#52525B]">Total: {questions.length}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <FileSpreadsheet size={14} className="mr-1" /> Template
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={questions.length === 0} data-testid="export-excel-btn">
            <Download size={14} className="mr-1" /> Export
          </Button>
          <label>
            <input type="file" accept=".xlsx" onChange={handleImport} className="hidden" data-testid="import-excel-input" />
            <Button variant="outline" size="sm" asChild disabled={importing}>
              <span className="cursor-pointer"><Upload size={14} className="mr-1" /> {importing ? 'Importing...' : 'Import'}</span>
            </Button>
          </label>
          <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="add-question-btn">
            <Plus size={16} className="mr-2" /> Add Question
          </Button>
        </div>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Question</TableHead>
              <TableHead>Correct</TableHead>
              <TableHead>Marks</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : questions.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#52525B]">No questions yet</TableCell></TableRow>
            ) : questions.map((q, i) => (
              <TableRow key={q.id} data-testid={`question-row-${q.id}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="max-w-xs truncate">{q.question_text}</TableCell>
                <TableCell><Badge className="bg-green-100 text-green-700 border-0">{q.correct_option?.toUpperCase()}</Badge></TableCell>
                <TableCell>{q.marks}</TableCell>
                <TableCell className="text-sm">{q.subject || '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(q)} data-testid={`edit-q-${q.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(q.id)} data-testid={`delete-q-${q.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit Question' : 'Add Question'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Question Text</Label><Textarea value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} rows={3} data-testid="q-text-input" /></div>
            <div><Label>Image URL (optional)</Label><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Option A</Label><Input value={form.option_a} onChange={e => setForm({ ...form, option_a: e.target.value })} data-testid="q-opt-a" /></div>
              <div><Label>Option B</Label><Input value={form.option_b} onChange={e => setForm({ ...form, option_b: e.target.value })} data-testid="q-opt-b" /></div>
              <div><Label>Option C</Label><Input value={form.option_c} onChange={e => setForm({ ...form, option_c: e.target.value })} data-testid="q-opt-c" /></div>
              <div><Label>Option D</Label><Input value={form.option_d} onChange={e => setForm({ ...form, option_d: e.target.value })} data-testid="q-opt-d" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Correct Answer</Label>
                <Select value={form.correct_option} onValueChange={v => setForm({ ...form, correct_option: v })}>
                  <SelectTrigger data-testid="q-correct-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a">A</SelectItem><SelectItem value="b">B</SelectItem>
                    <SelectItem value="c">C</SelectItem><SelectItem value="d">D</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Marks</Label><Input type="number" value={form.marks} onChange={e => setForm({ ...form, marks: Number(e.target.value) })} /></div>
              <div><Label>Subject</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="e.g., Physics" /></div>
            </div>
            <div><Label>Explanation</Label><Textarea value={form.explanation} onChange={e => setForm({ ...form, explanation: e.target.value })} rows={3} placeholder="Detailed explanation..." data-testid="q-explanation" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-question-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
